import * as THREE from 'three';
import { settings } from '@/config';

/**
 * PERLIN "SHADOW FLOW" STORY GRADE (2026-08-18, user: "I want the shadows flowing through each
 * and everything … the baked images get colorized with darker cinematic colours … that shader
 * effect called perlin transition").
 *
 * The first version of story mode just lerped every material's `color` toward a dark tint —
 * uniform, flat, and (in the user's words) "just making it dark". This replaces that with a real
 * transition: a 3D simplex-noise field, animated and sampled in WORLD SPACE, drives a dissolve
 * threshold so the darkness ARRIVES as smoky fronts creeping across the architecture rather than
 * a curtain being dimmed. Because the noise is world-space, a shadow crossing a wall continues
 * onto the floor it meets — it reads as one volume of smoke moving through the scene, not as a
 * per-object effect.
 *
 * HOW THE DISSOLVE WORKS. With noise `n` in 0..1 and the transition at `uAmount`:
 *
 *     mask = smoothstep(n, n + edge, uAmount * (1 + edge))
 *
 * At `uAmount` 0 the mask is 0 everywhere (nothing graded); at 1 it is 1 everywhere (fully
 * graded); in between, only the regions where the noise happens to be low have flipped — which
 * is exactly the ragged, organic front the user is describing. `edge` softens the boundary so it
 * is smoke, not a hard wipe.
 *
 * WHY THE PATCH HAPPENS AT LOAD, NOT ON OPEN. `onBeforeCompile` changes force a shader
 * recompile, which is a visible hitch. Every material is patched once during world build — with
 * `uAmount` at 0 the result is bit-identical to the ungraded material — so the recompiles land
 * inside the existing warm-up compile and opening a story only ever animates a uniform.
 *
 * Chains over any existing `onBeforeCompile` (the convention already used by `HeightFog`,
 * `hueSaturation` and the grass wind shader): the previous hook is captured and run first.
 *
 * ⚠️ NO BACKTICKS ANYWHERE IN THE GLSL BELOW - it lives in template literals, and a stray
 * backtick silently truncates the shader while the build still passes.
 */
export class StoryGrade {
  /** 0 = the scene as authored, 1 = fully inside the story's dark grade. */
  readonly amount = { value: 0 };
  /** Seconds, advanced every frame — this is what makes the shadows FLOW rather than sit. */
  readonly time = { value: 0 };
  /** Live-tunable look, shared by every patched material so one change moves the whole scene. */
  readonly tint = { value: new THREE.Color(settings.storyMode.grade.tint) };
  readonly rimTint = { value: new THREE.Color(settings.storyMode.grade.rimTint) };
  readonly rimAmount = { value: settings.storyMode.grade.rimAmount };
  readonly cloudDepth = { value: settings.storyMode.grade.cloudDepth };
  readonly darkness = { value: settings.storyMode.grade.darkness };
  readonly saturation = { value: settings.storyMode.grade.saturation };
  readonly tintAmount = { value: settings.storyMode.grade.tintAmount };
  readonly noiseScale = { value: settings.storyMode.grade.noiseScale };
  readonly flowSpeed = { value: settings.storyMode.grade.flowSpeed };
  readonly edge = { value: settings.storyMode.grade.edge };
  readonly warp = { value: settings.storyMode.grade.warp };
  readonly warpScale = { value: settings.storyMode.grade.warpScale };
  /** EMBLEM ENERGY (2026-09-01) — the emblem's fresnel-rim + flowing-filament language laid
   *  over every graded mesh. See `settings.storyMode.grade.emblem`. */
  readonly emblemFresnel = { value: settings.storyMode.grade.emblem.fresnel };
  readonly emblemPower = { value: settings.storyMode.grade.emblem.fresnelPower };
  readonly emblemColor = { value: new THREE.Color(settings.storyMode.grade.emblem.fresnelColor) };
  readonly emblemFlow = { value: settings.storyMode.grade.emblem.flow };
  readonly emblemFlowColor = { value: new THREE.Color(settings.storyMode.grade.emblem.flowColor) };
  readonly emblemFlowColor2 = { value: new THREE.Color(settings.storyMode.grade.emblem.flowColor2) };
  /** COLOUR RIPPLE (2026-09-02): the dissolve is gated by a sphere expanding from this
   *  world-space origin (the camera at the KNOW MORE click — set by Experience) as `amount`
   *  rises, so the colour change ripples outward from the viewer. */
  readonly rippleOrigin = { value: new THREE.Vector3(0, 0, 0) };
  readonly rippleMax = { value: settings.storyMode.grade.ripple.maxRadius };
  readonly rippleFeather = { value: settings.storyMode.grade.ripple.feather };
  /** EMISSION LINES (2026-09-02) — travelling iso-bands of the smoke field, white-hot core
   *  with an orange halo, flowing slowly across every graded surface. */
  readonly linesIntensity = { value: settings.storyMode.grade.lines.intensity };
  readonly linesDensity = { value: settings.storyMode.grade.lines.density };
  readonly linesSpeed = { value: settings.storyMode.grade.lines.speed };
  readonly linesWidth = { value: settings.storyMode.grade.lines.width };
  readonly linesColor = { value: new THREE.Color(settings.storyMode.grade.lines.color) };
  readonly linesCore = { value: new THREE.Color(settings.storyMode.grade.lines.coreColor) };
  /** Gain on the baked texture's luminance gradient — the neon-edge look's DETAIL lines. */
  readonly edgeGain = { value: settings.storyMode.grade.lines.edgeGain };
  /** VOICE PULSE (2026-09-02, the balance pass): 0..1, eased by Experience toward 1 while the
   *  narrator SPEAKS and back to the idle level in her silences — the ember energy swells with
   *  her voice and dies back down, so the world visibly listens to the words. */
  readonly pulse = { value: settings.storyMode.grade.pulse.idle };

  private readonly patched = new WeakSet<THREE.Material>();

  /** Patch one material. Safe to call repeatedly — a material is only ever patched once. */
  attach(material: THREE.Material): void {
    if (!material || this.patched.has(material)) return;
    // ShaderMaterial/RawShaderMaterial have no `<map_fragment>` chunk to hook, so they are graded
    // by their own uniforms instead (the sky and clouds — see `StoryMode.applyAtmosphere`).
    if ((material as THREE.ShaderMaterial).isShaderMaterial) return;
    this.patched.add(material);

    const prev = material.onBeforeCompile;
    material.onBeforeCompile = (shader, renderer) => {
      if (prev) prev.call(material, shader, renderer);

      shader.uniforms.uStoryAmount = this.amount;
      shader.uniforms.uStoryTime = this.time;
      shader.uniforms.uStoryTint = this.tint;
      shader.uniforms.uStoryRim = this.rimTint;
      shader.uniforms.uStoryRimAmount = this.rimAmount;
      shader.uniforms.uStoryCloudDepth = this.cloudDepth;
      shader.uniforms.uStoryDarkness = this.darkness;
      shader.uniforms.uStorySaturation = this.saturation;
      shader.uniforms.uStoryTintAmount = this.tintAmount;
      shader.uniforms.uStoryNoiseScale = this.noiseScale;
      shader.uniforms.uStoryFlowSpeed = this.flowSpeed;
      shader.uniforms.uStoryEdge = this.edge;
      shader.uniforms.uStoryWarp = this.warp;
      shader.uniforms.uStoryWarpScale = this.warpScale;
      shader.uniforms.uStoryEmblem = this.emblemFresnel;
      shader.uniforms.uStoryEmblemPower = this.emblemPower;
      shader.uniforms.uStoryEmblemColor = this.emblemColor;
      shader.uniforms.uStoryEmblemFlow = this.emblemFlow;
      shader.uniforms.uStoryEmblemFlowColor = this.emblemFlowColor;
      shader.uniforms.uStoryEmblemFlowColor2 = this.emblemFlowColor2;
      shader.uniforms.uStoryRippleOrigin = this.rippleOrigin;
      shader.uniforms.uStoryRippleMax = this.rippleMax;
      shader.uniforms.uStoryRippleFeather = this.rippleFeather;
      shader.uniforms.uStoryLines = this.linesIntensity;
      shader.uniforms.uStoryLineDensity = this.linesDensity;
      shader.uniforms.uStoryLineSpeed = this.linesSpeed;
      shader.uniforms.uStoryLineWidth = this.linesWidth;
      shader.uniforms.uStoryLineColor = this.linesColor;
      shader.uniforms.uStoryLineCore = this.linesCore;
      shader.uniforms.uStoryPulse = this.pulse;
      shader.uniforms.uStoryEdgeGain = this.edgeGain;

      // --- vertex: carry world position + world normal through to the fragment stage ---
      // The normal rides along for the EMBLEM pass's fresnel rim. The `normal` attribute is
      // declared by three for every material, so this is safe even on MeshBasicMaterial (which
      // never computes a vNormal of its own). Since the load-time auto-smooth pass
      // (utils/autoSmooth.ts) these are SMOOTH normals, so the rim curves around forms instead
      // of faceting per triangle the way the earlier derivative-reconstructed normal did.
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          '#include <common>\nvarying vec3 vStoryWorld;\nvarying vec3 vStoryNormal;',
        )
        .replace(
          '#include <project_vertex>',
          [
            '#include <project_vertex>',
            '{',
            '  vec4 storyPos = vec4( transformed, 1.0 );',
            '  vec3 storyNrm = normal;',
            '  #ifdef USE_INSTANCING',
            // Instanced grass: the per-blade matrix has to be applied here too, or every blade
            // in a patch would sample the noise at its patch origin and flicker as one block.
            '    storyPos = instanceMatrix * storyPos;',
            '    storyNrm = mat3( instanceMatrix ) * storyNrm;',
            '  #endif',
            '  vStoryWorld = ( modelMatrix * storyPos ).xyz;',
            '  vStoryNormal = mat3( modelMatrix ) * storyNrm;',
            '}',
          ].join('\n'),
        );

      // --- fragment: the flowing dissolve + the cinematic dark grade -------------------
      shader.fragmentShader = STORY_HEAD + shader.fragmentShader.replace(
        '#include <map_fragment>',
        '#include <map_fragment>\n' + STORY_APPLY,
      );
    };
    material.needsUpdate = true;
  }

  /** Advance the flow. Called every frame while a story is open (and while it eases out). */
  update(delta: number): void {
    this.time.value += delta;
  }

  /** Push live settings values onto the shared uniforms (debug GUI). */
  applySettings(): void {
    const g = settings.storyMode.grade;
    this.tint.value.set(g.tint);
    this.rimTint.value.set(g.rimTint);
    this.rimAmount.value = g.rimAmount;
    this.cloudDepth.value = g.cloudDepth;
    this.darkness.value = g.darkness;
    this.saturation.value = g.saturation;
    this.tintAmount.value = g.tintAmount;
    this.noiseScale.value = g.noiseScale;
    this.flowSpeed.value = g.flowSpeed;
    this.edge.value = g.edge;
    this.warp.value = g.warp;
    this.warpScale.value = g.warpScale;
    this.emblemFresnel.value = g.emblem.fresnel;
    this.emblemPower.value = g.emblem.fresnelPower;
    this.emblemColor.value.set(g.emblem.fresnelColor);
    this.emblemFlow.value = g.emblem.flow;
    this.emblemFlowColor.value.set(g.emblem.flowColor);
    this.emblemFlowColor2.value.set(g.emblem.flowColor2);
    this.rippleMax.value = g.ripple.maxRadius;
    this.rippleFeather.value = g.ripple.feather;
    this.linesIntensity.value = g.lines.intensity;
    this.linesDensity.value = g.lines.density;
    this.linesSpeed.value = g.lines.speed;
    this.linesWidth.value = g.lines.width;
    this.linesColor.value.set(g.lines.color);
    this.linesCore.value.set(g.lines.coreColor);
    this.edgeGain.value = g.lines.edgeGain;
  }
}

/**
 * Ashima / Stefan Gustavson's 3D simplex noise — the standard compact GLSL implementation
 * (webgl-noise, MIT). Simplex rather than classic Perlin: same organic character, cheaper in 3D
 * and free of the axis-aligned artefacts that would read as grid lines creeping over flat walls.
 */
const STORY_HEAD = /* glsl */ `
  varying vec3 vStoryWorld;
  varying vec3 vStoryNormal;
  uniform float uStoryAmount;
  uniform float uStoryTime;
  uniform vec3  uStoryTint;
  uniform vec3  uStoryRim;
  uniform float uStoryRimAmount;
  uniform float uStoryCloudDepth;
  uniform float uStoryDarkness;
  uniform float uStorySaturation;
  uniform float uStoryTintAmount;
  uniform float uStoryNoiseScale;
  uniform float uStoryFlowSpeed;
  uniform float uStoryEdge;
  uniform float uStoryWarp;
  uniform float uStoryWarpScale;
  uniform float uStoryEmblem;
  uniform float uStoryEmblemPower;
  uniform vec3  uStoryEmblemColor;
  uniform float uStoryEmblemFlow;
  uniform vec3  uStoryEmblemFlowColor;
  uniform vec3  uStoryEmblemFlowColor2;
  uniform vec3  uStoryRippleOrigin;
  uniform float uStoryRippleMax;
  uniform float uStoryRippleFeather;
  uniform float uStoryLines;
  uniform float uStoryLineDensity;
  uniform float uStoryLineSpeed;
  uniform float uStoryLineWidth;
  uniform vec3  uStoryLineColor;
  uniform vec3  uStoryLineCore;
  uniform float uStoryPulse;
  uniform float uStoryEdgeGain;

  vec3 storyMod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 storyMod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 storyPermute(vec4 x) { return storyMod289(((x * 34.0) + 1.0) * x); }
  vec4 storyTaylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float storySimplex(vec3 v) {
    const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);

    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);

    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;

    i = storyMod289(i);
    vec4 p = storyPermute(storyPermute(storyPermute(
               i.z + vec4(0.0, i1.z, i2.z, 1.0))
             + i.y + vec4(0.0, i1.y, i2.y, 1.0))
             + i.x + vec4(0.0, i1.x, i2.x, 1.0));

    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);

    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);

    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);

    vec4 norm = storyTaylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;

    vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
  }

  /*
   * FOUR-OCTAVE FBM — this is what turns a smooth blob field into CLOUD.
   *
   * Each octave doubles the frequency and roughly halves the amplitude, so the big slow masses
   * carry progressively finer wisps inside them. Crucially each octave drifts on its OWN vector:
   * a single shared drift makes the whole field slide like a printed sheet, whereas independent
   * drifts make the layers shear past each other, which is what reads as billowing.
   */
  float storyFbm(vec3 p, float t) {
    float sum = 0.0;
    float amp = 0.5;
    float freq = 1.0;
    vec3 drift = vec3(t * 0.21, t * 0.10, t * -0.16);
    for (int i = 0; i < 3; i++) {
      sum += storySimplex(p * freq + drift * (1.0 + float(i) * 0.55)) * amp;
      freq *= 2.07;   /* not exactly 2 - avoids the octaves lining up into visible lattices */
      amp *= 0.5;
    }
    return sum;
  }

  /*
   * DOMAIN WARP - the step that makes this read as LIQUID rather than as drifting fog.
   *
   * The cloud field is not sampled at the surface position; the position is first DISPLACED by a
   * second, slower noise field. Plain FBM gives soft masses that swell and fade where they
   * stand. Warping the lookup makes the field fold through itself, so the darkness pours across
   * a surface and streams around its forms - motion with direction and current in it.
   *
   * Only two warp components (x and z, the horizontal plane) rather than three: vertical warping
   * mostly reads as noise rather than flow, and skipping it saves a whole FBM evaluation per
   * fragment - this runs on every graded surface, so the third axis is not worth its cost.
   */
  vec3 storyWarp(vec3 p, float t) {
    if (uStoryWarp <= 0.001) return p;
    vec3 q = p * uStoryWarpScale;
    float wx = storyFbm(q + vec3(0.0, 1.7, 3.1), t * 0.6);
    float wz = storyFbm(q + vec3(5.2, 4.6, 0.0), t * 0.6);
    return p + vec3(wx, wx * 0.35, wz) * uStoryWarp;
  }

  /* Cloud density in 0..1. The ridge fold (1 - |n|) is the classic billow trick: it turns the
     zero-crossings of the noise into bright creases, which read as the lit edges of smoke. */
  float storyFlow(vec3 p, float t) {
    float n = storyFbm(storyWarp(p, t), t);
    float billow = 1.0 - abs(n);
    return clamp(mix(n * 0.5 + 0.5, billow, 0.62), 0.0, 1.0);
  }
`;

const STORY_APPLY = /* glsl */ `
  {
    if (uStoryAmount > 0.0001) {
      float storyN = storyFlow(vStoryWorld * uStoryNoiseScale, uStoryTime * uStoryFlowSpeed);
      /* Dissolve threshold: 0 leaves everything untouched, 1 covers everything, and in between
         only the low-density regions have turned - the ragged smoky front. */
      float storyMask = smoothstep(storyN, storyN + uStoryEdge, uStoryAmount * (1.0 + uStoryEdge));

      /* COLOUR RIPPLE (2026-09-02, user: "when I click KNOW MORE - a ripple effect, the ripple
         changes the colors"). The dissolve is additionally gated by a sphere expanding from
         the VIEWER's position at the click: as uStoryAmount rises the wavefront travels
         outward through the scene, so the dark visibly ripples away from you (and pulls back
         toward you on close). The soft feathered front + the noise dissolve above together
         read as a wave of night washing over the architecture. */
      float storyRippleR = uStoryAmount * (uStoryRippleMax + uStoryRippleFeather);
      float storyRipple = 1.0 - smoothstep(
        storyRippleR - uStoryRippleFeather,
        storyRippleR,
        distance(vStoryWorld, uStoryRippleOrigin));
      storyMask *= storyRipple;

      /* How THICK the cloud is right here, once it has arrived. Drives both the extra darkening
         and the colour, so the black masses visibly roll across a surface instead of the whole
         surface dimming together. */
      float storyThick = clamp((1.0 - storyN) * uStoryAmount, 0.0, 1.0);

      vec3 storyBase = diffuseColor.rgb;
      float storyLuma = dot(storyBase, vec3(0.2126, 0.7152, 0.0722));

      /* Desaturate and crush, then crush FURTHER where the cloud is thick. */
      vec3 storyDark = mix(vec3(storyLuma), storyBase, uStorySaturation);
      storyDark *= uStoryDarkness * (1.0 - uStoryCloudDepth * storyThick);

      /* Two-colour dark fantasy: near-black indigo in the thick, cold violet in the torn thin
         edges. Mixing on (1 - thickness) means the colour travels WITH the smoke. */
      vec3 storyCol = mix(uStoryRim, uStoryTint, storyThick);
      storyDark = mix(storyDark, storyCol * (storyLuma * 0.7 + 0.3), uStoryTintAmount);
      storyDark += uStoryRim * (1.0 - storyThick) * uStoryRimAmount * 0.16 * storyMask;

      diffuseColor.rgb = mix(storyBase, storyDark, storyMask);

      /*
       * NEON EDGES (2026-09-02 v3 — the reference-screenshot rework, user: "see how they
       * implemented shader mode … the world dark, only edges — same thing in my scene with
       * ORANGE"). The whole architecture sinks to near-pure black (the retuned grade above)
       * and ONLY ITS EDGES burn — the scene becomes a luminous orange sketch of itself:
       *
       *  - SILHOUETTES: fresnel of the smooth vertex normal (auto-smoothed at load) lights
       *    every form's outline against the black, like the reference's glowing ridgelines.
       *  - DETAIL LINES: the screen-space luminance gradient of the BAKED TEXTURE itself
       *    (fwidth of storyLuma) traces brick seams, stair nosings and painted borders as
       *    fine contour lines — the interior "drawing" of the reference.
       *
       * The colour fades deep-ember -> hot amber with edge strength; the flowing field
       * shimmers the glow so the sketch feels alive, and the VOICE PULSE breathes the whole
       * drawing with the narrator. All scaled by storyMask, so the sketch ripples in from the
       * viewer and vanishes without residue at amount 0.
       */
      if (uStoryLines > 0.001) {
        vec3 storyNrm = vStoryNormal;
        if (dot(storyNrm, storyNrm) < 1e-6) storyNrm = cross(dFdx(vStoryWorld), dFdy(vStoryWorld));
        storyNrm = normalize(storyNrm);
        vec3 storyView = normalize(cameraPosition - vStoryWorld);
        if (dot(storyNrm, storyView) < 0.0) storyNrm = -storyNrm;
        float storyFres = pow(clamp(1.0 - dot(storyView, storyNrm), 0.0, 1.0), uStoryEmblemPower);
        /* A wall seen at a grazing angle is "silhouette" across its WHOLE face - which lit
           broad slabs solid amber. Gating the fresnel by the flowing field breaks those faces
           into drifting patches of energy, while true edge-on silhouette lines stay lit. */
        storyFres *= 0.25 + 0.75 * smoothstep(0.3, 0.85, storyN);
        /* DETAIL LINES FADE WITH DISTANCE. fwidth(luma) measures the texture gradient PER
           SCREEN PIXEL - at range one pixel spans a huge patch of atlas, so every distant
           facade saturated to a solid glowing slab. Up close the seams draw; far away only
           the fresnel silhouette survives, exactly like the reference's outlined mountains. */
        float storyDetailFade = 1.0 - smoothstep(45.0, 160.0, distance(cameraPosition, vStoryWorld));
        float storyTexEdge = clamp(fwidth(storyLuma) * uStoryEdgeGain, 0.0, 1.0) * storyDetailFade;
        float storyEdge = clamp(storyFres * uStoryEmblem + storyTexEdge, 0.0, 1.3);
        float storyShimmer = 0.55 + 0.45 * storyN;
        vec3 storyEdgeCol = mix(uStoryLineColor, uStoryLineCore, clamp(storyEdge, 0.0, 1.0));
        diffuseColor.rgb += storyEdgeCol * storyEdge * storyShimmer
                          * uStoryLines * (0.3 + 0.7 * uStoryPulse) * storyMask;
      }
    }
  }
`;
